"use client";

import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

type PaginationProps = {
    page: number;
    totalPages: number;
};

const Pagination = ({ page, totalPages }: PaginationProps) => {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const handlePageChange = (newPage: number) => {
        const params = new URLSearchParams(searchParams);
        params.set("page", String(newPage));
        router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    };

    // Generate a small window of page numbers (e.g., 1 ... 4 5 6 ... 10)
    const getPageNumbers = () => {
        const delta = 2; // how many pages to show around current
        const pages: (number | string)[] = [];

        for (let i = Math.max(2, page - delta); i <= Math.min(totalPages - 1, page + delta); i++) {
            pages.push(i);
        }

        if (page - delta > 2) {
            pages.unshift("...");
        }
        if (page + delta < totalPages - 1) {
            pages.push("...");
        }

        pages.unshift(1);
        if (totalPages > 1) pages.push(totalPages);

        return pages;
    };

    return (
        <div className="flex justify-center mt-8">
            <nav className="flex items-center gap-2">
                <Button
                    variant={"outline"}
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page <= 1}
                >
                    <ChevronLeft />
                </Button>

                {getPageNumbers().map((p, idx) =>
                    typeof p === "number" ? (
                        <Button
                            key={idx}
                            onClick={() => handlePageChange(p)}
                            variant={p === page ? "default" : "outline"}
                            className={
                                p !== page ? "hover:bg-gray-100 text-white" : "text-white"
                            }
                        >
                            {p}
                        </Button>
                    ) : (
                        <Button
                            key={idx}
                            variant={"outline"}
                            className="text-white"
                        >
                            {p}
                        </Button>
                    )
                )}

                <Button
                    variant={"outline"}
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= totalPages}
                >
                    <ChevronRight />
                </Button>
            </nav>
        </div>
    );
};

export default Pagination;
